import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

import LibCameraPhoto from "../../lib";

interface CameraResolution {
	width?: number;
	height?: number;
}

interface DataUriConfig {
	sizeFactor?: number;
	imageType?: string;
	imageCompression?: number;
	isImageMirror?: boolean;
}

type CameraError = Error | null;

export function useLibCameraPhoto(
	videoRef: RefObject<HTMLVideoElement>,
	idealFacingMode?: string,
	idealResolution?: CameraResolution,
	isMaxResolution = false,
): [MediaStream | null, CameraError, CameraError, (config: DataUriConfig) => string] {
	const cameraRef = useRef<LibCameraPhoto | null>(null);
	const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
	const [cameraStartError, setCameraStartError] = useState<CameraError>(null);

	useEffect(() => {
		if (!videoRef.current) return;
		cameraRef.current = new LibCameraPhoto(videoRef.current);

		return () => {
			cameraRef.current = null;
		};
	}, [videoRef]);

	useEffect(() => {
		const camera = cameraRef.current;
		if (!camera) return;

		let isActive = true;
		void (async () => {
			try {
				const stream = isMaxResolution
					? await camera.startCameraMaxResolution(idealFacingMode)
					: await camera.startCamera(idealFacingMode, idealResolution);

				if (isActive && videoRef.current) {
					setMediaStream(stream);
					setCameraStartError(null);
				} else {
					await camera.stopCamera().catch(() => undefined);
				}
			} catch (error) {
				if (isActive) setCameraStartError(error instanceof Error ? error : new Error(String(error)));
			}
		})();

		return () => {
			isActive = false;
			void camera.stopCamera().catch(() => undefined);
			setMediaStream(null);
		};
	}, [idealFacingMode, idealResolution, isMaxResolution, videoRef]);

	const getDataUri = useCallback((config: DataUriConfig) => {
		if (!cameraRef.current) return "";
		return cameraRef.current.getDataUri(config);
	}, []);

	return [mediaStream, cameraStartError, null, getDataUri];
}
